import React from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useUI } from '@/context/UIContext'
import { useCart } from '@/context/CartContext'
import { 
  XMarkIcon,
  UserIcon,
  ShoppingBagIcon,
  HeartIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'
import Button from '@/components/UI/Button'
import Logo from '@/components/UI/Logo'

/**
 * MobileSidebar - Mobile navigation sidebar
 * 
 * @component
 */
const MobileSidebar = () => {
  const { isAuthenticated, user, logout } = useAuth()
  const { sidebar, toggleSidebar, toggleModal } = useUI()
  const { itemCount } = useCart()

  const handleLogout = () => {
    logout()
    toggleSidebar('mobileMenu')
  }

  const handleSearchClick = () => {
    toggleSidebar('mobileMenu')
    toggleModal('searchModal')
  }

  if (!sidebar.mobileMenu) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        onClick={() => toggleSidebar('mobileMenu')}
      />
      
      {/* Sidebar */}
      <div className="fixed top-0 right-0 h-full w-80 bg-white shadow-xl z-50 lg:hidden transform transition-transform duration-300 ease-in-out">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <Logo 
              size="sm" 
              showText={true} 
              textSize="sm"
              variant="mobile"
              className="hover:scale-105 transition-transform duration-300"
            />
            <button
              onClick={() => toggleSidebar('mobileMenu')}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* User Section */}
          <div className="p-4 border-b border-gray-200">
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-primary-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-sm text-gray-500">{user?.email}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Welcome to Maurifood</p>
                <div className="flex flex-col space-y-2">
                  <Link to="/login" onClick={() => toggleSidebar('mobileMenu')}>
                    <Button variant="outline" className="w-full">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register" onClick={() => toggleSidebar('mobileMenu')}>
                    <Button variant="primary" className="w-full">
                      Sign Up
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto">
            <nav className="p-4 space-y-2">
              {/* Search */}
              <button
                onClick={handleSearchClick}
                className="flex items-center space-x-3 w-full p-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <MagnifyingGlassIcon className="w-5 h-5" />
                <span>Search Products</span>
              </button>

              {/* Categories */}
              <Link
                to="/categories"
                onClick={() => toggleSidebar('mobileMenu')}
                className="flex items-center space-x-3 w-full p-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <ShoppingBagIcon className="w-5 h-5" />
                <span>Categories</span>
              </Link>

              {/* Products */}
              <Link
                to="/products"
                onClick={() => toggleSidebar('mobileMenu')}
                className="flex items-center space-x-3 w-full p-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <ShoppingBagIcon className="w-5 h-5" />
                <span>All Products</span>
              </Link>

              {/* Cart */}
              <button
                onClick={() => {
                  toggleSidebar('mobileMenu')
                  toggleModal('cartDrawer')
                }}
                className="flex items-center justify-between w-full p-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <ShoppingBagIcon className="w-5 h-5" />
                  <span>Cart</span>
                </div>
                {itemCount > 0 && (
                  <span className="bg-primary-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {itemCount > 99 ? '99+' : itemCount}
                  </span>
                )}
              </button>

              {/* User Menu Items (if authenticated) */}
              {isAuthenticated && (
                <>
                  <Link
                    to="/profile"
                    onClick={() => toggleSidebar('mobileMenu')}
                    className="flex items-center space-x-3 w-full p-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <UserIcon className="w-5 h-5" />
                    <span>Profile</span>
                  </Link>
                  
                  <Link
                    to="/orders"
                    onClick={() => toggleSidebar('mobileMenu')}
                    className="flex items-center space-x-3 w-full p-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <ShoppingBagIcon className="w-5 h-5" />
                    <span>Orders</span>
                  </Link>
                  
                  <Link
                    to="/wishlist"
                    onClick={() => toggleSidebar('mobileMenu')}
                    className="flex items-center space-x-3 w-full p-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <HeartIcon className="w-5 h-5" />
                    <span>Wishlist</span>
                  </Link>
                </>
              )}

              {/* About */}
              <Link
                to="/about"
                onClick={() => toggleSidebar('mobileMenu')}
                className="flex items-center space-x-3 w-full p-3 text-left text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <CogIcon className="w-5 h-5" />
                <span>About Us</span>
              </Link>
            </nav>
          </div>

          {/* Footer */}
          {isAuthenticated && (
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-3 w-full p-3 text-left text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default MobileSidebar
